import React from 'react'

import {
  css, lighten, styled, ToggleButton as MuiToggleButton, ToggleButtonProps as BaseToggleButtonProps, Tooltip, TooltipProps,
} from '@mui/material'

export type ToggleButtonProps = Omit<BaseToggleButtonProps, 'disableRipple'> & {
  primary?: boolean
  secondary?: boolean
  text?: boolean
  round?: boolean
  tooltipTitle?: string
  slotProps?: {
    tooltip?: Omit<TooltipProps, 'title' | 'children'>
  }
}

// es-lint-disable-next-line @typescript-eslint/no-unused-vars
export const ToggleButton: React.FC<ToggleButtonProps> = ({ primary, secondary, round, text, tooltipTitle, slotProps, ...props }) => {
  const toggleButtonProps: BaseToggleButtonProps = props

  toggleButtonProps.disableRipple = true

  if (primary) {
    toggleButtonProps.color = 'primary'
  } else if (secondary) {
    toggleButtonProps.color = 'secondary'
  }

  return (
    <Tooltip title={tooltipTitle || ''} arrow {...slotProps?.tooltip}>
      <StyledToggleButton
        size="small"
        $round={round}
        $text={text}
        {...toggleButtonProps}
      />
    </Tooltip>
  )
}

const StyledToggleButton = styled(MuiToggleButton, {
  shouldForwardProp: (prop) => prop !== '$round' && prop !== '$text',
}) <{ $round?: boolean, $text?: boolean }>`
  text-transform: none;
  font-weight: 500;
  height: ${props => props.theme.typography.pxToRem(32)};
  color: ${props => props.theme.palette.text.primary};
  border-radius: ${props => props.$round ? '1000px' : '4px'}

  ${({ $text }) => $text && css`border: none;`}

  &.MuiButtonBase-root.MuiToggleButton-root.Mui-selected {
    border-color: ${props => lighten(props.theme.palette.primary.main, 0.4)};
  }

  &.MuiToggleButton-root:hover {
    background-color: ${props => props.theme.palette.action.hover}
  }

  &:after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 4px;
    background-color: ${(props) => props.theme.palette.action.hover};
    opacity: 0;
  }

  &:hover {
    &:after {
      opacity: 1;
      background-color: ${(props) => props.theme.palette.action.hover};
      transform: scaleX(1) scaleY(1) perspective(1px);
    }
  }

  &.MuiToggleButtonGroup-firstButton {
    &:after {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
  }

  &.MuiToggleButtonGroup-lastButton {
    &:after {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
  }
`